/**
 * `window.Polst` imperative global (POL-588).
 *
 * Hosts that build DOM dynamically — SPAs, portal widgets, CMS blocks —
 * need an imperative handle into the POLST widget. Auto-hydration
 * (POL-587) only works for markup that's present at `DOMContentLoaded`
 * (or added to an observed subtree); direct `mount()` requires a
 * bundler. This module bridges the gap: it installs a single global,
 * `window.Polst`, as a side effect of the widget bundle loading.
 *
 * Contract:
 *   - SSR safe: every top-level expression is guarded by
 *     `typeof window !== "undefined"`. Importing the module in a Node
 *     context is a no-op — no global mutation.
 *   - Single source of truth: a pre-existing `window.Polst` whose
 *     `__polstSource === "widget"` is preserved. Second bundle
 *     execution / HMR reload / multi-install does not duplicate.
 *   - Re-mount safe: mounting into a container that is already
 *     hydrated destroys the previous handle first.
 *   - Delegation only: render logic reuses `mountAutoTarget` from
 *     `./auto-hydrate` (POL-587). Data methods delegate to the
 *     `./api` module. No duplication.
 *   - Types are exported so the native SDK (POL-558) can reuse them.
 */

import {
  configureRestClient,
  getBrandPolsts as apiGetBrandPolsts,
  getCampaign as apiGetCampaign,
  getPolst as apiGetPolst,
  votePolst as apiVotePolst,
  type BrandListResponse,
  type CampaignSummary,
  type ClientOptions,
  type PolstSummary,
  type VoteOption,
  type VoteResult,
} from "./api";
import {
  mountAutoTarget,
  type AutoHydrateConfig,
  type MountAutoHandle,
  type MountAutoKind,
  type MountAutoTargetOptions,
} from "./auto-hydrate";
import type { BrandFeedMode } from "./brand-feed";
import { configureOrigins, type OriginOverrides } from "./config";
import type { RestClientOptions } from "./rest-client";
import type { ThemeMode } from "./theme";

// ─── Public types ───────────────────────────────────────────────────────────

/**
 * Options for `window.Polst.mount`. Exactly one of `polstId`,
 * `campaignId`, or `brandSlug` must be set — they select the mode
 * renderer (polst card / campaign player / brand feed).
 */
export interface MountGlobalOptions {
  polstId?: string;
  campaignId?: string;
  brandSlug?: string;
  /** Brand-feed mode — polsts-only, campaigns-only, or mixed. */
  mode?: BrandFeedMode;
  theme?: ThemeMode;
  accent?: string;
  size?: string;
  hideTitle?: boolean;
  hideBrand?: boolean;
  autoAdvance?: boolean;
  /**
   * POL-788: per-call API origin override. When set, this mount's
   * REST-fetch hits this origin instead of the widget global
   * (`configureOrigins` / `window.Polst.configure({ origins })`) or
   * the build-time default. Per-call only — does NOT mutate the global.
   * Useful for SDK consumers who want a single render pointed at
   * staging without configuring a process-wide override.
   */
  apiOrigin?: string;
  onVote?: (event: unknown) => void;
  onComplete?: (event: unknown) => void;
}

export interface MountGlobalHandle {
  /** Detach the rendered widget and dispose the Shadow DOM root. */
  destroy(): void;
  /**
   * Destroy the current render and remount with fresh options. This is
   * a full replacement, not an in-place patch — simpler to reason about
   * than a granular diff, and matches how hosts typically swap props.
   */
  update(opts: MountGlobalOptions): void;
}

/**
 * The shape of `window.Polst`. Exported so the SDK (POL-558) and any
 * host typings can consume it directly.
 */
export interface WindowPolst {
  /** Semver string of the loaded widget bundle. */
  readonly version: string;
  mount(target: Element | string, opts: MountGlobalOptions): MountGlobalHandle;
  getPolst(id: string, options?: ClientOptions): Promise<PolstSummary>;
  getCampaign(id: string, options?: ClientOptions): Promise<CampaignSummary>;
  getBrandPolsts(
    slug: string,
    options?: ClientOptions,
  ): Promise<BrandListResponse<PolstSummary>>;
  vote(polstId: string, option: VoteOption): Promise<VoteResult>;
  /**
   * Configure the shared REST client and optional origin overrides
   * (app host for share/watermark links, API host for REST calls).
   * Origins default to the build-time constants baked into the bundle.
   */
  configure(options: WindowPolstConfigureOptions): void;
}

/**
 * Host-visible options for `window.Polst.configure()`. Union of REST
 * client overrides and the narrower `origins` block — callers can pass
 * either set independently.
 */
export interface WindowPolstConfigureOptions extends RestClientOptions {
  origins?: OriginOverrides;
}

// ─── Internal markers ───────────────────────────────────────────────────────

const HYDRATED_ATTR = "data-polst-hydrated";
const SOURCE_MARKER = "__polstSource";
const HANDLE_KEY = "__polstHandle";
const LOG_PREFIX = "[polst]";

interface PolstGlobalWithMarker extends WindowPolst {
  readonly [SOURCE_MARKER]?: "widget";
}

interface ContainerWithHandle extends Element {
  [HANDLE_KEY]?: MountAutoHandle | undefined;
}

/** Build-time-defined by the esbuild pipeline; mirrors `./index.ts`. */
declare const __POLST_WIDGET_VERSION__: string;

function resolveVersion(): string {
  return typeof __POLST_WIDGET_VERSION__ !== "undefined"
    ? __POLST_WIDGET_VERSION__
    : "0.0.0";
}

// ─── Target resolution ──────────────────────────────────────────────────────

function resolveTarget(target: Element | string, doc: Document): Element {
  if (typeof target === "string") {
    const el = doc.querySelector(target);
    if (!el) {
      throw new TypeError(`Polst.mount: target "${target}" not found`);
    }
    return el;
  }
  if (
    !target ||
    typeof (target as Element).appendChild !== "function"
  ) {
    throw new TypeError("Polst.mount: target must be an Element or selector");
  }
  return target;
}

// ─── Opts → AutoHydrateConfig ───────────────────────────────────────────────

/**
 * Translate the host-friendly `MountGlobalOptions` into the internal
 * `AutoHydrateConfig` that `mountAutoTarget` consumes. Validates that
 * exactly one of `polstId | campaignId | brandSlug` is set.
 */
function buildConfig(opts: MountGlobalOptions): AutoHydrateConfig {
  const candidates: Array<{ kind: MountAutoKind; id: string | undefined }> = [
    { kind: "polst", id: opts.polstId },
    { kind: "campaign", id: opts.campaignId },
    { kind: "brand", id: opts.brandSlug },
  ];
  const chosen = candidates.filter(
    (c) => typeof c.id === "string" && c.id.trim() !== "",
  );
  if (chosen.length === 0) {
    throw new TypeError(
      "Polst.mount: opts must include one of polstId, campaignId, or brandSlug",
    );
  }
  if (chosen.length > 1) {
    throw new TypeError(
      "Polst.mount: opts must set exactly one of polstId, campaignId, or brandSlug",
    );
  }

  const picked = chosen[0];
  if (!picked) {
    throw new TypeError(
      "Polst.mount: internal — chosen.length > 0 was checked above",
    );
  }
  const config: AutoHydrateConfig = {
    kind: picked.kind,
    // chosen is non-empty → id is the same string we filtered on.
    id: picked.id as string,
  };
  if (opts.theme !== undefined) config.theme = opts.theme;
  if (opts.accent !== undefined) config.accent = opts.accent;
  if (opts.size !== undefined) config.size = opts.size;
  if (opts.hideTitle !== undefined) config.hideTitle = opts.hideTitle;
  if (opts.hideBrand !== undefined) config.hideBrand = opts.hideBrand;
  if (opts.autoAdvance !== undefined) config.autoAdvance = opts.autoAdvance;
  if (opts.mode !== undefined) config.mode = opts.mode;
  if (opts.apiOrigin !== undefined) config.apiOrigin = opts.apiOrigin;
  if (opts.onVote !== undefined) config.onVote = opts.onVote;
  if (opts.onComplete !== undefined) config.onComplete = opts.onComplete;
  return config;
}

// ─── Re-mount replacement ───────────────────────────────────────────────────

/**
 * If the container already hosts a widget instance (either from
 * auto-hydration or a previous `mount` call), tear it down. Clearing
 * the hydration marker is the signal to downstream code that the slot
 * is free again.
 */
function clearExistingHandle(container: ContainerWithHandle): void {
  const existing = container[HANDLE_KEY];
  if (existing) {
    try {
      existing.destroy();
    } catch (err) {
      // A broken renderer shouldn't block the next mount.
      console.error(`${LOG_PREFIX} previous handle.destroy() failed:`, err);
    }
    container[HANDLE_KEY] = undefined;
  }
  if (container.getAttribute(HYDRATED_ATTR) === "1") {
    container.removeAttribute(HYDRATED_ATTR);
  }
}

// ─── mount() ─────────────────────────────────────────────────────────────────

type MountHostOverrides = Pick<
  MountAutoTargetOptions,
  "document" | "fetchPolstOverride" | "fetchCampaignOverride"
>;

/**
 * Core mount implementation. Split from the `window.Polst.mount` entry
 * point so tests can inject a custom `document` + fetch overrides
 * without touching the real global (jsdom shares a single `window`
 * across test files).
 */
export function mountGlobal(
  target: Element | string,
  opts: MountGlobalOptions,
  overrides: MountHostOverrides = {},
): MountGlobalHandle {
  if (typeof document === "undefined" && !overrides.document) {
    throw new Error("Polst.mount: no document available");
  }
  const doc = overrides.document ?? document;
  const container = resolveTarget(target, doc) as ContainerWithHandle;
  const config = buildConfig(opts);

  // Re-mount: destroy any previous tenant of this container first.
  clearExistingHandle(container);

  // Stamp the hydration attr synchronously so a concurrent
  // MutationObserver tick (POL-587) does not double-hydrate while our
  // async renderer is still fetching.
  container.setAttribute(HYDRATED_ATTR, "1");

  const mountOptions: MountAutoTargetOptions = { ...config };
  if (overrides.document) mountOptions.document = overrides.document;
  if (overrides.fetchPolstOverride)
    mountOptions.fetchPolstOverride = overrides.fetchPolstOverride;
  if (overrides.fetchCampaignOverride)
    mountOptions.fetchCampaignOverride = overrides.fetchCampaignOverride;

  // Track the promise so `destroy()` can race cleanly with a mid-flight
  // fetch: we keep a local "destroyed" flag; when the async mount
  // resolves after destroy() fired, we immediately tear down.
  let destroyed = false;
  const pending: Promise<MountAutoHandle> = mountAutoTarget(
    container,
    mountOptions,
  )
    .then((handle) => {
      if (destroyed) {
        try {
          handle.destroy();
        } catch {
          /* swallow — we're destroying anyway */
        }
        throw new Error("destroyed");
      }
      container[HANDLE_KEY] = handle;
      return handle;
    })
    .catch((err) => {
      // Mount failure leaves the slot available for the next try.
      if (!destroyed) {
        container.removeAttribute(HYDRATED_ATTR);
      }
      throw err;
    });
  // Prevent an unhandled-rejection warning when the host ignores mount errors.
  pending.catch(() => undefined);

  const handle: MountGlobalHandle = {
    destroy(): void {
      destroyed = true;
      const live = container[HANDLE_KEY];
      if (live) {
        try {
          live.destroy();
        } catch (err) {
          console.error(`${LOG_PREFIX} destroy() failed:`, err);
        }
        container[HANDLE_KEY] = undefined;
      }
      container.removeAttribute(HYDRATED_ATTR);
    },
    update(newOpts: MountGlobalOptions): void {
      handle.destroy();
      const replacement = mountGlobal(container, newOpts, overrides);
      // Patch this handle's methods to forward to the new one so any
      // reference the host holds keeps working after update().
      handle.destroy = replacement.destroy.bind(replacement);
      handle.update = replacement.update.bind(replacement);
    },
  };

  return handle;
}

// ─── Installer ──────────────────────────────────────────────────────────────

/**
 * Build the `window.Polst` object. Separate from the install so tests
 * can exercise the surface without touching a shared `window`.
 */
export function createWindowPolst(): WindowPolst {
  const api: WindowPolst = {
    version: resolveVersion(),
    mount(target, opts) {
      return mountGlobal(target, opts);
    },
    getPolst(id, options) {
      return apiGetPolst(id, options);
    },
    getCampaign(id, options) {
      return apiGetCampaign(id, options);
    },
    getBrandPolsts(slug, options) {
      return apiGetBrandPolsts(slug, options);
    },
    vote(polstId, option) {
      return apiVotePolst(polstId, option);
    },
    configure(options) {
      if (options.origins) {
        configureOrigins(options.origins);
      }
      configureRestClient(options);
    },
  };
  // Non-enumerable marker so host-side Object.keys() doesn't surprise
  // developers with a leading-underscore property. The value is
  // intentionally opaque ("widget") so a future fork of this bundle
  // can distinguish itself and refuse to overwrite the official one.
  Object.defineProperty(api, SOURCE_MARKER, {
    value: "widget",
    enumerable: false,
    writable: false,
    configurable: false,
  });
  return api;
}

/**
 * Install `window.Polst` — idempotent and SSR-safe. Returns the live
 * global (or `null` under SSR). Repeat calls return the existing
 * reference so HMR or a duplicate bundle load doesn't clobber state.
 */
export function installGlobal(): WindowPolst | null {
  if (typeof window === "undefined") return null;

  const host = window as unknown as { Polst?: PolstGlobalWithMarker };
  const existing = host.Polst;
  if (existing && existing[SOURCE_MARKER] === "widget") {
    return existing;
  }

  const api = createWindowPolst();
  try {
    host.Polst = api;
  } catch {
    // Some sandboxes (Trusted Types, frozen window) reject the
    // assignment. Failing silently is the right move — the widget
    // still works via its other surfaces (auto-hydrate, direct import).
    return null;
  }
  return api;
}

// Side effect: install on import, but only when we're in a browser.
// Node / SSR imports are no-ops — the guard inside installGlobal()
// returns null without touching any global.
installGlobal();
